import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import authService from '../services/authService';

/**
 * Protected Route Component
 * Wraps routes that require authentication and email verification
 * 
 * Usage:
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <Dashboard />
 *   </ProtectedRoute>
 * } />
 * 
 * For admin-only routes (deprecated - use clubOwnerOnly instead):
 * <Route path="/admin" element={
 *   <ProtectedRoute adminOnly={true}>
 *     <AdminDashboard />
 *   </ProtectedRoute>
 * } />
 * 
 * For club-owner-only routes:
 * <Route path="/club-management" element={
 *   <ProtectedRoute clubOwnerOnly={true}>
 *     <ClubManagement />
 *   </ProtectedRoute>
 * } />
 * 
 * For super-admin-only routes:
 * <Route path="/super-admin" element={
 *   <ProtectedRoute superAdminOnly={true}>
 *     <SuperAdminDashboard />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute = ({ children, adminOnly = false, clubOwnerOnly = false, superAdminOnly = false }) => {
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  
  const isAuthenticated = authService.isAuthenticated();
  const isAdmin = authService.isAdmin();
  const isClubOwner = authService.isClubOwner();
  const isSuperAdmin = authService.isSuperAdmin();
  const isVerified = authService.isVerified();
  const user = authService.getCurrentUser();

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }

  if (!isVerified) {
    // Show verification required message
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Email Verification Required
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Please Verify Your Email
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                We've sent a verification email to <strong>{user?.email}</strong>.
                Please check your inbox and click the verification link to access your dashboard.
              </p>
              
              {verificationMessage && (
                <div className={`mt-4 rounded-md p-4 ${
                  verificationMessage.includes('sent') 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  <div className="text-sm">{verificationMessage}</div>
                </div>
              )}

              <div className="mt-6">
                <button
                  onClick={async () => {
                    setResendingVerification(true);
                    setVerificationMessage('');
                    
                    try {
                      const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/auth/resend-verification`,
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({ email: user?.email }),
                        }
                      );

                      const data = await response.json();

                      if (response.ok) {
                        setVerificationMessage('Verification email sent! Please check your inbox.');
                      } else {
                        setVerificationMessage(data.message || 'Failed to resend verification email.');
                      }
                    } catch (err) {
                      setVerificationMessage('An error occurred. Please try again.');
                    } finally {
                      setResendingVerification(false);
                    }
                  }}
                  disabled={resendingVerification}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                </button>
              </div>

              <div className="mt-4">
                <button
                  onClick={async () => {
                    await authService.logout();
                    window.location.href = '/login';
                  }}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (adminOnly && !isAdmin) {
    // Redirect to user dashboard if trying to access admin route without admin privileges
    return <Navigate to="/dashboard" replace />;
  }

  if (clubOwnerOnly && !isClubOwner) {
    // Redirect to user dashboard if trying to access club owner route without club owner role
    return <Navigate to="/dashboard" replace />;
  }

  if (superAdminOnly && !isSuperAdmin) {
    // Redirect to user dashboard if trying to access super admin route without super admin role
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
